import React, { createContext, useContext, useState, useEffect } from "react";

interface ThemeContextType {
  theme: "light" | "dark" | "auto";
  setTheme: (theme: "light" | "dark" | "auto") => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize theme from localStorage synchronously to prevent flash
  const getInitialTheme = (): "light" | "dark" | "auto" => {
    const savedTheme = localStorage.getItem("flehs-theme");
    if (
      savedTheme === "light" ||
      savedTheme === "dark" ||
      savedTheme === "auto"
    ) {
      return savedTheme;
    }
    return "auto"; // Default to auto if not set
  };

  const [theme, setTheme] = useState<"light" | "dark" | "auto">(
    getInitialTheme
  );

  useEffect(() => {
    localStorage.setItem("flehs-theme", theme);

    const root = document.documentElement;

    // Always set the base theme attributes regardless of light/dark choice
    root.setAttribute("data-bs-theme-base", "neutral");
    root.setAttribute("data-bs-theme-primary", "cyan");
    root.setAttribute("data-bs-theme-font", "sans-serif");

    // Set the light/dark theme based on user preference
    if (theme === "auto") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      root.setAttribute("data-bs-theme", prefersDark ? "dark" : "light");
    } else {
      root.setAttribute("data-bs-theme", theme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
